import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiGetUserInventory } from '../../../api/userApi';
import type { CardAccount, UserInventoryCard } from '../../../types/models/userCredit';

const STORAGE_URL = process.env.REACT_APP_STORAGE_URL ?? '';

const toUrl = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : STORAGE_URL + path) : null;

const RARITY_COLOR: Record<string, string> = {
  common: 'rgba(255,255,255,0.5)',
  rare: '#4fc3f7',
  epic: '#ce93d8',
  legendary: 'gold',
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const Modal = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: gold;
  margin: 0 0 4px;
`;

const ModalSubtitle = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 20px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
  &:hover { color: #fff; }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 20px 24px;
`;

const StatusText = styled.p`
  color: rgba(255, 255, 255, 0.45);
  text-align: center;
  margin: 40px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
`;

const CardItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardImageWrapper = styled.div`
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background-color: #111;
  aspect-ratio: 63 / 88;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
  font-size: 12px;
`;

const QuantityBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  background-color: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.2);
`;

const RarityBadge = styled.div<{ rarity: string }>`
  position: absolute;
  bottom: 6px;
  left: 6px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ rarity }) => RARITY_COLOR[rarity] ?? '#fff'};
  text-transform: capitalize;
  background-color: rgba(0,0,0,0.65);
  padding: 2px 6px;
  border-radius: 10px;
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.4;
  word-break: break-word;
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  account: CardAccount;
  onClose: () => void;
}

const InventoryModal: React.FC<Props> = ({ account, onClose }) => {
  const [cards, setCards] = useState<UserInventoryCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    apiGetUserInventory(account.user.id)
      .then((res) => { if (isMounted) setCards(res.data); })
      .catch(() => { if (isMounted) setError('Errore durante il caricamento dell\'inventario.'); });
    return () => { isMounted = false; };
  }, [account.user.id]);

  const totalCards = cards ? cards.reduce((sum, c) => sum + c.quantity, 0) : 0;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseBtn onClick={onClose}>✕</CloseBtn>

        <ModalHeader>
          <ModalTitle>Inventario Carte</ModalTitle>
          <ModalSubtitle>
            {account.user.username}
            {account.user.nickname_forum ? ` — ${account.user.nickname_forum}` : ''}
            {cards !== null && ` · ${cards.length} carte distinte, ${totalCards} totali`}
          </ModalSubtitle>
        </ModalHeader>

        <ModalBody>
          {!cards && !error && <StatusText>Caricamento...</StatusText>}
          {error && <StatusText style={{ color: '#dc3545' }}>{error}</StatusText>}

          {cards && cards.length === 0 && (
            <StatusText>Questo utente non possiede ancora nessuna carta.</StatusText>
          )}

          {cards && cards.length > 0 && (
            <Grid>
              {cards.map((card) => {
                const img = toUrl(card.fronte_path);
                const wrestlers = card.wrestlers.map((w) => w.name).join(', ');
                return (
                  <CardItem key={card.id}>
                    <CardImageWrapper>
                      {img
                        ? <CardImage src={img} alt={wrestlers || `Carta #${card.id}`} />
                        : <CardPlaceholder>No img</CardPlaceholder>
                      }
                      <QuantityBadge>x{card.quantity}</QuantityBadge>
                      <RarityBadge rarity={card.rarity}>{card.rarity}</RarityBadge>
                    </CardImageWrapper>
                    {wrestlers && <CardMeta>{wrestlers}</CardMeta>}
                  </CardItem>
                );
              })}
            </Grid>
          )}
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default InventoryModal;
